import { createQueryString } from '~/domains/common/utils/queryString';

import { toPlayerPropSectionResponse } from '../domains/player-props/utils/playerPropSectionResponse';

import { marketServiceInstance } from './market.service.instance';
import type {
  PredictionMarketDiscoverResponse,
  MarketGroupDetailResponse,
  MarketSubCategoriesResponse,
  GetPredictionMarketsDiscoverParams,
  GetPredictionMarketsParams,
  GetPlayerPropSectionParams,
  GetPromotedMarketsParams,
  PredictionMarketsResponse,
  PlayerPropListingResponse,
  PlayerPropMarketsResponse,
  PlayerPropGroupDetailResponse,
  PublicMarketGroup,
  PortfolioSummaryResponse,
} from './interfaces/market.types';
import type {
  GetMarketTransactionsParams,
  MarketTransactionsResponse,
} from './interfaces/market.transaction.types';
import type {
  GetMarketStatementsParams,
  MarketStatementsResponse,
} from './interfaces/market.statement.types';
import { normalizeMarketGroupMarkets, normalizeMoneylineMarket } from './interfaces/market.types';

export const getPredictionMarketsDiscover = async (
  params?: GetPredictionMarketsDiscoverParams
): Promise<PredictionMarketDiscoverResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.sub_category) {
    const subCategoryValue = Array.isArray(params.sub_category)
      ? params.sub_category.join(',')
      : params.sub_category;
    searchParams.append('sub_category', subCategoryValue);
  }

  if (params?.search) {
    searchParams.append('search', params.search);
  }

  if (params?.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }

  if (params?.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }

  const queryString = searchParams.toString();
  const url = `/discover${queryString ? `?${queryString}` : ''}`;

  const response = await marketServiceInstance.get<PredictionMarketDiscoverResponse>(url);
  const data = response.data;
  data.discover = data.discover.map((section) => ({
    ...section,
    market_groups: section.market_groups.map(normalizeMarketGroupMarkets),
  }));
  return data;
};

export const getMarketSubCategories = async (options?: {
  signal?: AbortSignal;
}): Promise<MarketSubCategoriesResponse> => {
  const response = await marketServiceInstance.get<MarketSubCategoriesResponse>('/sub_categories', {
    signal: options?.signal,
  });

  return response.data;
};

export const getMarketGroupById = async (
  marketGroupId: string,
  options?: { signal?: AbortSignal }
): Promise<MarketGroupDetailResponse> => {
  const response = await marketServiceInstance.get<MarketGroupDetailResponse>(
    `/prediction_markets/${marketGroupId}`,
    { signal: options?.signal }
  );
  const data = response.data;
  data.market_group = normalizeMarketGroupMarkets(data.market_group);
  return data;
};

export const getPlayerPropGroupById = async (
  playerGroupId: string,
  options?: { signal?: AbortSignal }
): Promise<PlayerPropGroupDetailResponse> => {
  const response = await marketServiceInstance.get<PlayerPropGroupDetailResponse>(
    `/prediction_markets/${playerGroupId}`,
    { signal: options?.signal }
  );
  return response.data;
};

export const getGameGroupByEvent = async (
  eventId: string,
  options?: { signal?: AbortSignal }
): Promise<PublicMarketGroup | null> => {
  const url = `/prediction_markets${createQueryString({
    event_id: eventId,
    type: 'gameline',
    limit: 1,
  })}`;

  const response = await marketServiceInstance.get<PredictionMarketsResponse>(url, {
    signal: options?.signal,
  });

  return response.data.data[0] ?? null;
};

export const getPredictionMarkets = async (
  params: GetPredictionMarketsParams
): Promise<PredictionMarketsResponse> => {
  const searchParams = new URLSearchParams();

  searchParams.append('sub_category', params.sub_category);

  if (params.types?.length) {
    searchParams.append('type', params.types.join(','));
  }

  if (params.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }

  if (params.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }

  const queryString = searchParams.toString();
  const url = `/prediction_markets?${queryString}`;

  const response = await marketServiceInstance.get<PredictionMarketsResponse>(url);
  const data = response.data;
  data.data = data.data.map(normalizeMarketGroupMarkets);
  return data;
};

export const getPlayerPropMarkets = async (
  eventId: string,
  options?: { signal?: AbortSignal }
): Promise<PlayerPropMarketsResponse> => {
  const url = `/events/${eventId}/details${createQueryString({
    type: 'player_prop',
    group_by: 'prop_type',
  })}`;

  const response = await marketServiceInstance.get<PlayerPropMarketsResponse>(url, {
    signal: options?.signal,
  });
  return response.data;
};

export const getPlayerPropSection = async (
  { eventId, propType, teamId, limit, offset }: GetPlayerPropSectionParams,
  options?: { signal?: AbortSignal }
): Promise<PlayerPropMarketsResponse> => {
  const url = `/prediction_markets${createQueryString({
    event_id: eventId,
    prop_type: propType,
    team_id: teamId,
    limit,
    offset,
  })}`;

  const response = await marketServiceInstance.get<PlayerPropListingResponse>(url, {
    signal: options?.signal,
  });

  return toPlayerPropSectionResponse(response.data, { propType, teamId });
};

export const getPromotedPredictionMarkets = async (
  params?: GetPromotedMarketsParams
): Promise<PredictionMarketsResponse> => {
  const url = `/prediction_markets/promoted${createQueryString({
    type: params?.types,
    limit: params?.limit,
  })}`;

  const response = await marketServiceInstance.get<PredictionMarketsResponse>(url);
  const data = response.data;
  data.data = data.data.map(normalizeMarketGroupMarkets);
  return data;
};

export const getPortfolioSummary = async (): Promise<PortfolioSummaryResponse> => {
  const response = await marketServiceInstance.get<PortfolioSummaryResponse>('/portfolio/summary');
  return response.data;
};

export const getMarketTransactions = async (
  params?: GetMarketTransactionsParams
): Promise<MarketTransactionsResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }

  if (params?.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }

  if (params?.from) {
    searchParams.append('from', params.from);
  }

  if (params?.to) {
    searchParams.append('to', params.to);
  }

  const queryString = searchParams.toString();
  const url = `/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await marketServiceInstance.get<MarketTransactionsResponse>(url);
  const txData = response.data;
  txData.data = txData.data.map((tx) => ({
    ...tx,
    market: normalizeMoneylineMarket(tx.market),
  }));
  return txData;
};

export const getMarketStatements = async (
  params: GetMarketStatementsParams,
  options?: { signal?: AbortSignal }
): Promise<MarketStatementsResponse> => {
  const searchParams = new URLSearchParams({
    from_year: params.from_year.toString(),
    from_month: params.from_month.toString(),
    to_year: params.to_year.toString(),
    to_month: params.to_month.toString(),
  });

  if (params.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }

  if (params.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }

  const response = await marketServiceInstance.get<MarketStatementsResponse>(
    `/statements?${searchParams.toString()}`,
    { signal: options?.signal }
  );

  return response.data;
};

export const downloadMarketStatement = async (statementId: string): Promise<Blob> => {
  const response = await marketServiceInstance.get<Blob>(`/statements/${statementId}/download`, {
    responseType: 'blob',
  });

  return response.data;
};
